export const MetadataTypes = {
    STRING: 'string',
    NUMBER: 'number',
    BOOLEAN: 'boolean',
    JSON: 'json',
};

export function getMetadataTypes() {
    return Object.values(MetadataTypes);
}

/**
 * Metadata are stored in the additionalData property of the shipping method.
 * Those keys already have a dedicated field in the form (see AdditionalDataFields),
 * they must not be editable a second time through the metadata table.
 */
export const managedAdditionalDataKeys = [
    'sandbox',
    'customerNumber',
    'carrierServiceId',
    'returnCarrierServiceId',
    'api_url',
    'api_username',
    'api_key',
    'agency_code',
    'client_code',
    'product_code',
    'sender_name',
    'sender_country_code',
    'sender_post_code',
    'sender_city',
];

export function getMetadataType(value) {
    if (typeof value === 'boolean') {
        return MetadataTypes.BOOLEAN;
    }

    if (typeof value === 'number') {
        return MetadataTypes.NUMBER;
    }

    if (value !== null && typeof value === 'object') {
        return MetadataTypes.JSON;
    }

    return MetadataTypes.STRING;
}

/**
 * In the form, a metadata value is always handled as a string,
 * it is casted back to its real type on submit (see toAdditionalData).
 */
export function toMetadataRows(additionalData) {
    if (!additionalData || Array.isArray(additionalData)) {
        return [];
    }

    return Object.entries(additionalData)
        .filter(([key, value]) => !managedAdditionalDataKeys.includes(key) && value !== null)
        .map(([key, value]) => {
            const type = getMetadataType(value);

            return {
                key,
                type,
                value: type === MetadataTypes.JSON ? JSON.stringify(value) : String(value),
            };
        });
}

export function castMetadataValue(value, type) {
    switch (type) {
        case MetadataTypes.NUMBER:
            return value === '' || isNaN(Number(value)) ? null : Number(value);
        case MetadataTypes.BOOLEAN:
            return value === 'true';
        case MetadataTypes.JSON:
            try {
                return JSON.parse(value);
            } catch (e) {
                return null;
            }
        default:
            return value;
    }
}

export function toAdditionalData(metadata) {
    if (!Array.isArray(metadata)) {
        return {};
    }

    return metadata.reduce((additionalData, {key, type, value}) => {
        if (!key || key.trim() === '') {
            return additionalData;
        }

        additionalData[key.trim()] = castMetadataValue(value, type);

        return additionalData;
    }, {});
}

/**
 * Merges the metadata table into additionalData and removes the metadata
 * property, which only exists in the form and not in the API.
 */
export function applyMetadataToValues(values) {
    const {metadata, ...rest} = values;

    return {
        ...rest,
        additionalData: {
            ...(Array.isArray(rest.additionalData) ? {} : rest.additionalData),
            ...toAdditionalData(metadata),
        },
    };
}
